import React from "react";
import { CTAButton, SunRays, Reveal } from "@/lib/uiHelpers";
import { TRUST_BAR, HERO_SNAPSHOT } from "@/constants/landingCopy";
import { Check, ChevronDown, Sun } from "lucide-react";

function HeroBackground() {
  return (
    <>
      <img
        src="/images/install-roof-ocean.jpg"
        alt="Solar panels installed on a Maui rooftop overlooking the ocean"
        className="absolute inset-0 w-full h-full object-cover"
        fetchPriority="high"
      />
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(26,43,74,0.82) 0%, rgba(26,43,74,0.86) 55%, rgba(26,43,74,0.95) 100%)",
        }}
      />
      <SunRays color="#F5A623" opacity={0.14} originX="100%" originY="0%" />
      <div
        className="absolute inset-0"
        style={{
          background: "radial-gradient(1100px 560px at 88% -10%, rgba(245,166,35,0.20), transparent 60%)",
        }}
      />
    </>
  );
}

function SnapshotCard() {
  return (
    <div
      className="rounded-3xl p-7 md:p-8 backdrop-blur-md"
      style={{
        background: "rgba(16,26,46,0.55)",
        border: "1px solid rgba(255,255,255,0.16)",
        boxShadow: "0 30px 60px -25px rgba(0,0,0,0.7)",
      }}
    >
      <p className="font-head text-sm font-bold uppercase" style={{ color: "#F5A623", letterSpacing: "0.16em" }}>
        Your Program Snapshot
      </p>
      <div className="mt-5 space-y-4">
        {HERO_SNAPSHOT.map((row) => (
          <div key={row.big} className="flex items-baseline gap-3 border-b border-white/10 pb-4 last:border-0 last:pb-0">
            <span className="font-head font-extrabold text-3xl md:text-4xl whitespace-nowrap" style={{ color: "#fff" }}>{row.big}</span>
            <span className="font-body text-sm text-white/75">{row.small}</span>
          </div>
        ))}
      </div>
      <div
        className="mt-6 rounded-xl px-4 py-3 text-sm font-semibold text-center"
        style={{ background: "rgba(245,166,35,0.16)", color: "#ffd98a", border: "1px solid rgba(245,166,35,0.4)" }}
      >
        Two ways to fund it &mdash; a Home Equity Agreement or a conventional credit union program.
      </div>
    </div>
  );
}

function HeroCopy() {
  return (
    <div className="lg:col-span-7 text-center lg:text-left">
      <Reveal>
        <span
          className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs md:text-sm font-semibold"
          style={{ background: "rgba(245,166,35,0.15)", color: "#F5A623", border: "1px solid rgba(245,166,35,0.35)" }}
        >
          <Sun size={15} fill="#F5A623" /> Booking Discovery Calls for Maui Homeowners This Week
        </span>
      </Reveal>

      <Reveal delay={0.08}>
        <h1 className="font-head mt-6 text-white font-extrabold leading-[1.05] text-4xl sm:text-5xl lg:text-6xl">
          Go Solar on Maui
          <span className="block" style={{ color: "#F5A623" }}>With No New Monthly Payment</span>
        </h1>
      </Reveal>

      <Reveal delay={0.12}>
        <p className="font-head mt-4 text-lg sm:text-xl font-bold text-white">
          Plus a new roof, HVAC, remodel, or any home upgrade &mdash; funded the same way.
        </p>
      </Reveal>

      <Reveal delay={0.16}>
        <p className="font-body mt-4 text-base sm:text-lg text-white/85 max-w-xl mx-auto lg:mx-0">
          Put your home&apos;s equity to work &mdash; go solar with no monthly payments and no income
          requirement through our Home Equity Agreement option, or choose a conventional credit
          union program. The same funding can cover a new roof, HVAC, battery backup, or remodel.
          One 10-minute call shows you which path fits.
        </p>
      </Reveal>

      <Reveal delay={0.24}>
        <div className="mt-6 flex flex-wrap justify-center lg:justify-start gap-x-5 gap-y-2">
          {TRUST_BAR.map((item) => (
            <span key={item} className="flex items-center gap-1.5 text-sm text-white/80">
              <Check size={16} style={{ color: "#2ecc71" }} strokeWidth={3} />
              {item}
            </span>
          ))}
        </div>
      </Reveal>

      <Reveal delay={0.32}>
        <div className="mt-9">
          <CTAButton testId="hero-cta" variant="gold" className="w-full sm:w-auto">
            &rarr; Book My Free 10-Min Call Below
          </CTAButton>
        </div>
      </Reveal>
    </div>
  );
}

export default function Hero() {
  return (
    <section
      id="hero"
      className="relative min-h-[100svh] flex items-center overflow-hidden"
      style={{ backgroundColor: "#1A2B4A" }}
    >
      <HeroBackground />
      <div className="container-max relative z-10 py-20 md:py-24">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          <HeroCopy />
          <div className="lg:col-span-5">
            <Reveal delay={0.2}>
              <SnapshotCard />
            </Reveal>
          </div>
        </div>
      </div>

      <button
        type="button"
        aria-label="Scroll to booking"
        onClick={() => document.getElementById("booking")?.scrollIntoView({ behavior: "smooth" })}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 text-white/70 hover:text-white"
      >
        <ChevronDown size={30} className="chevron-bounce" />
      </button>
    </section>
  );
}
